import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "../../../lib/supabase";

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const VALID_PROMO_CODE = "QUANTUMEGIFT";

// Inner form that uses Stripe hooks
const CheckoutForm = ({ onSuccess, onCancel, customerInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState(customerInfo?.email || "");
  const [name, setName] = useState(customerInfo?.name || "");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");

  const handleApplyPromo = () => {
    const trimmed = promoCode?.trim()?.toUpperCase();
    if (!trimmed) {
      setPromoError("Please enter a promo code.");
      setPromoSuccess("");
      return;
    }
    if (trimmed === VALID_PROMO_CODE) {
      setPromoApplied(true);
      setPromoError("");
      setPromoSuccess("🎉 100% off applied! Your blueprint is free.");
    } else {
      setPromoApplied(false);
      setPromoError("Invalid promo code. Please check and try again.");
      setPromoSuccess("");
    }
  };

  const handleRemovePromo = () => {
    setPromoCode("");
    setPromoApplied(false);
    setPromoError("");
    setPromoSuccess("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate name
    if (!name?.trim()) {
      setNameError("Full name is required.");
      return;
    }
    setNameError("");

    // Validate email
    if (!email?.trim()) {
      setEmailError("Email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");

    setIsProcessing(true);
    setErrorMessage("");

    try {
      // If promo code gives 100% off, skip Stripe payment entirely
      if (promoApplied) {
        const result = await supabase?.functions?.invoke("create-payment-intent", {
          body: {
            paymentData: {
              amount: 0,
              currency: "usd",
              description: "QuantuMe Prospectus Blueprint — AI-Powered Income Strategy (Promo: QUANTUMEGIFT)",
              promoCode: VALID_PROMO_CODE,
              promotekit_referral: window?.promotekit_referral || null,
            },
            customerInfo: {
              userId: null,
              name: name?.trim(),
              email: email?.trim(),
              stripeCustomerId: null,
            },
          },
        });

        let intentData = result?.data;
        let intentError = result?.error;

        if (intentError) {
          let errMsg = intentError?.message || "Unable to apply promo. Please try again.";
          try {
            const ctx = intentError?.context;
            if (ctx) {
              const body = await ctx?.json?.();
              if (body?.error) errMsg = body?.error;
            }
          } catch (_) {}
          setErrorMessage(errMsg);
          setIsProcessing(false);
          return;
        }

        if (intentData?.error) {
          setErrorMessage(intentData?.error || "Unable to apply promo. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Promo success — treat as paid with a synthetic intent object
        onSuccess(
          { id: intentData?.paymentIntentId || `promo_${Date.now()}`, status: "succeeded" },
          { name: name?.trim(), email: email?.trim() }
        );
        setIsProcessing(false);
        return;
      }

      if (!stripe || !elements) return;

      // Trigger form validation and wallet collection first (required by Stripe)
      const { error: submitError } = await elements?.submit();
      if (submitError) {
        setErrorMessage(submitError?.message || "Please complete your payment details.");
        setIsProcessing(false);
        return;
      }

      // Create payment intent with the actual collected name + email
      let intentData = null;
      let intentError = null;

      try {
        const result = await supabase?.functions?.invoke("create-payment-intent", {
          body: {
            paymentData: {
              amount: 22,
              currency: "usd",
              description: "QuantuMe Prospectus Blueprint — AI-Powered Income Strategy",
              promotekit_referral: window?.promotekit_referral || null,
            },
            customerInfo: {
              userId: null,
              name: name?.trim(),
              email: email?.trim(),
              stripeCustomerId: null,
            },
          },
        });
        intentData = result?.data;
        intentError = result?.error;

        if (intentError) {
          let errMsg = intentError?.message || "Unable to initialize payment. Please try again.";
          try {
            const ctx = intentError?.context;
            if (ctx) {
              const body = await ctx?.json?.();
              if (body?.error) errMsg = body?.error;
            }
          } catch (_) {}
          console.error("Edge function error:", intentError, errMsg);
          setErrorMessage(errMsg);
          setIsProcessing(false);
          return;
        }
      } catch (invokeErr) {
        console.error("Invoke threw:", invokeErr);
        setErrorMessage(invokeErr?.message || "Unable to initialize payment. Please try again.");
        setIsProcessing(false);
        return;
      }

      if (intentData?.error) {
        console.error("Intent data error:", intentData?.error);
        setErrorMessage(intentData?.error || "Unable to initialize payment. Please try again.");
        setIsProcessing(false);
        return;
      }

      const clientSecret = intentData?.clientSecret;
      if (!clientSecret) {
        console.error("No clientSecret in response:", intentData);
        setErrorMessage("Unable to initialize payment. Please try again.");
        setIsProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe?.confirmPayment({
        clientSecret,
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/professional-input-form`,
          payment_method_data: {
            billing_details: {
              name: name?.trim(),
              email: email?.trim(),
              phone: "",
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(error?.message || "Payment failed. Please try again.");
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        // Confirm on backend
        await supabase?.functions?.invoke("confirm-payment", {
          body: { paymentIntentId: paymentIntent?.id },
        });
        onSuccess(paymentIntent, { name: name?.trim(), email: email?.trim() });
      }
    } catch (err) {
      console.error("Payment error:", err);
      setErrorMessage(err?.message || "An unexpected error occurred. Please try again.");
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Name Field */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="checkout-name"
          className="text-sm font-semibold"
          style={{ color: "#283ba3", fontFamily: "var(--font-heading)" }}
        >
          Full Name <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          id="checkout-name"
          type="text"
          value={name}
          onChange={(e) => { setName(e?.target?.value); setNameError(""); }}
          placeholder="Your full name"
          required
          className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none transition-all"
          style={{
            borderColor: nameError ? "#dc2626" : "rgba(40,59,163,0.25)",
            backgroundColor: "#fffbf5",
            color: "#1f2937",
            fontFamily: "var(--font-body)",
            boxShadow: nameError ? "0 0 0 2px rgba(220,38,38,0.12)" : "none",
          }}
        />
        {nameError && (
          <p className="text-xs" style={{ color: "#dc2626", fontFamily: "var(--font-caption)" }}>
            {nameError}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="checkout-email"
          className="text-sm font-semibold"
          style={{ color: "#283ba3", fontFamily: "var(--font-heading)" }}
        >
          Email Address <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          id="checkout-email"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e?.target?.value); setEmailError(""); }}
          placeholder="your@email.com"
          required
          className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none transition-all"
          style={{
            borderColor: emailError ? "#dc2626" : "rgba(40,59,163,0.25)",
            backgroundColor: "#fffbf5",
            color: "#1f2937",
            fontFamily: "var(--font-body)",
            boxShadow: emailError ? "0 0 0 2px rgba(220,38,38,0.12)" : "none",
          }}
        />
        {emailError && (
          <p className="text-xs" style={{ color: "#dc2626", fontFamily: "var(--font-caption)" }}>
            {emailError}
          </p>
        )}
      </div>

      {/* Promo Code Field */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="checkout-promo"
          className="text-sm font-semibold"
          style={{ color: "#283ba3", fontFamily: "var(--font-heading)" }}
        >
          Promo Code <span className="text-xs font-normal" style={{ color: "#9ca3af" }}>(optional)</span>
        </label>
        {promoApplied ? (
          <div
            className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm border"
            style={{ borderColor: "#16a34a", backgroundColor: "rgba(22,163,74,0.06)" }}
          >
            <span className="font-semibold" style={{ color: "#16a34a", fontFamily: "var(--font-body)" }}>
              {promoSuccess}
            </span>
            <button
              type="button"
              onClick={handleRemovePromo}
              className="text-xs underline ml-2 flex-shrink-0"
              style={{ color: "#9ca3af", fontFamily: "var(--font-caption)" }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              id="checkout-promo"
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e?.target?.value); setPromoError(""); }}
              placeholder="Enter promo code"
              className="flex-1 px-3 py-2.5 rounded-xl text-sm border outline-none transition-all uppercase"
              style={{
                borderColor: promoError ? "#dc2626" : "rgba(40,59,163,0.25)",
                backgroundColor: "#fffbf5",
                color: "#1f2937",
                fontFamily: "var(--font-body)",
                boxShadow: promoError ? "0 0 0 2px rgba(220,38,38,0.12)" : "none",
                letterSpacing: "0.05em",
              }}
              onKeyDown={(e) => { if (e?.key === "Enter") { e?.preventDefault(); handleApplyPromo(); } }}
            />
            <button
              type="button"
              onClick={handleApplyPromo}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all flex-shrink-0"
              style={{
                borderColor: "rgba(40,59,163,0.35)",
                color: "#283ba3",
                backgroundColor: "rgba(40,59,163,0.06)",
                fontFamily: "var(--font-heading)",
              }}
            >
              Apply
            </button>
          </div>
        )}
        {promoError && (
          <p className="text-xs" style={{ color: "#dc2626", fontFamily: "var(--font-caption)" }}>
            {promoError}
          </p>
        )}
      </div>

      {/* Only show Stripe payment fields when no 100% promo applied */}
      {!promoApplied && (
        <PaymentElement
          options={{
            layout: "tabs",
            fields: {
              billingDetails: {
                email: "never",
                phone: "never",
              },
            },
            defaultValues: {
              billingDetails: {
                name: name || customerInfo?.name || "",
                email: email || customerInfo?.email || "",
              },
            },
          }}
        />
      )}

      {errorMessage && (
        <p className="text-sm px-3 py-2 rounded-lg" style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.08)", fontFamily: "var(--font-caption)" }}>
          {errorMessage}
        </p>
      )}
      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border transition-all"
          style={{
            borderColor: "rgba(40,59,163,0.25)",
            color: "#283ba3",
            backgroundColor: "transparent",
            fontFamily: "var(--font-heading)",
            opacity: isProcessing ? 0.5 : 1,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={(!stripe && !promoApplied) || isProcessing}
          className="flex-1 py-2.5 px-4 rounded-xl text-sm font-bold transition-all"
          style={{
            background: isProcessing
              ? "rgba(40,59,163,0.5)"
              : promoApplied
              ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
              : "linear-gradient(135deg, #283ba3 0%, #1e2d8a 100%)",
            color: "#fff",
            fontFamily: "var(--font-heading)",
            fontWeight: 700,
            cursor: ((!stripe && !promoApplied) || isProcessing) ? "not-allowed" : "pointer",
          }}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing...
            </span>
          ) : promoApplied ? (
            "Unlock Free Blueprint →"
          ) : (
            "Complete Payment — $22"
          )}
        </button>
      </div>
    </form>
  );
};

// A minimal Elements wrapper that doesn't need a clientSecret upfront
// We use stripe.js in "deferred" mode — clientSecret is provided at confirmPayment time
const StripeElementsWrapper = ({ children, customerInfo, onSuccess, onCancel, promoApplied }) => {
  const options = {
    mode: "payment",
    amount: 2200,
    currency: "usd",
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#283ba3",
        colorBackground: "#fffbf5",
        colorText: "#1f2937",
        borderRadius: "12px",
        fontFamily: "inherit",
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm
        onSuccess={onSuccess}
        onCancel={onCancel}
        customerInfo={customerInfo}
        promoApplied={promoApplied}
      />
    </Elements>
  );
};

// Main payment modal component
const BlueprintPaymentModal = ({ isOpen, onClose, onPaymentSuccess, customerInfo }) => {
  const handleClose = () => {
    onClose();
  };

  const handleSuccess = (paymentIntent, collectedInfo) => {
    onPaymentSuccess(paymentIntent, collectedInfo);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={(e) => { if (e?.target === e?.currentTarget) handleClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 md:p-8 relative overflow-y-auto"
        style={{ backgroundColor: "#fffbf5", border: "1.5px solid rgba(40,59,163,0.18)", boxShadow: "0 8px 40px rgba(40,59,163,0.18)", maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #283ba3 0%, #1e2d8a 100%)" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <span className="font-bold text-base" style={{ color: "#283ba3", fontFamily: "var(--font-heading)" }}>
                Unlock Your Blueprint
              </span>
            </div>
            <button
              onClick={handleClose}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:bg-gray-100"
              style={{ color: "#9ca3af" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ backgroundColor: "rgba(40,59,163,0.06)" }}>
            <span className="text-sm font-medium" style={{ color: "#283ba3", fontFamily: "var(--font-body)" }}>
              QuantuMe Prospectus Blueprint
            </span>
            <span className="text-base font-bold" style={{ color: "#d3ac35", fontFamily: "var(--font-heading)" }}>
              $22.00
            </span>
          </div>
        </div>

        <StripeElementsWrapper
          customerInfo={customerInfo}
          onSuccess={handleSuccess}
          onCancel={handleClose}
          promoApplied={false}
        />

        <p className="text-xs text-center mt-4" style={{ color: "#9ca3af", fontFamily: "var(--font-caption)" }}>
          🔒 Secured by Stripe · SSL encrypted
        </p>
      </div>
    </div>
  );
};

export default BlueprintPaymentModal;
